import { useTranslation } from 'react-i18next'
import { ImageConceptSlotCard } from './ImageConceptSlotCard'

interface ImageConceptProps {
  concept: {
    product_classification: string
    color_palette: { primary: string; accent: string; contrast: string; rationale: string }
    hero_creative_concept: { description: string; frech_elements: string[]; anti_patterns: string[] }
    slots: Array<{
      position: number
      scene: string
      rationale: string
      emotion: string
      thumbnail_check: string
      scroll_stop_score: number
      anti_patterns: string[]
    }>
    rejected_concepts: Array<{ type: string; reason: string }>
    story_arc: string
  }
}

export function ImageConceptOverview({ concept }: ImageConceptProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold dark:text-white">{t('imageConcept.title')}</h3>
        <p className="text-sm text-gray-500 dark:text-neutral-400">{concept.product_classification}</p>
      </div>

      <StoryArcSection storyArc={concept.story_arc} />
      <ColorPaletteRow palette={concept.color_palette} />
      <HeroCreativeCard hero={concept.hero_creative_concept} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {concept.slots.map((slot) => (
          <ImageConceptSlotCard key={slot.position} slot={slot} />
        ))}
      </div>

      {concept.rejected_concepts.length > 0 && (
        <RejectedConceptsList rejected={concept.rejected_concepts} />
      )}
    </div>
  )
}

function StoryArcSection({ storyArc }: { storyArc: string }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-neutral-700/50 p-3">
      <p className="text-xs font-medium text-gray-500 dark:text-neutral-400 mb-1">
        {t('imageConcept.storyArc')}
      </p>
      <p className="text-sm dark:text-neutral-200">{storyArc}</p>
    </div>
  )
}

function ColorPaletteRow({ palette }: { palette: ImageConceptProps['concept']['color_palette'] }) {
  const { t } = useTranslation()
  const colors = [palette.primary, palette.accent, palette.contrast]
  return (
    <div className="flex items-center gap-3">
      <p className="text-xs font-medium text-gray-500 dark:text-neutral-400">
        {t('imageConcept.colorPalette')}:
      </p>
      {colors.map((color, i) => (
        <div key={i} className="flex items-center gap-1">
          <div className="h-5 w-5 rounded border border-gray-200 dark:border-neutral-600" style={{ backgroundColor: color }} />
          <span className="text-xs text-gray-400 dark:text-neutral-500">{color}</span>
        </div>
      ))}
    </div>
  )
}

function HeroCreativeCard({ hero }: { hero: ImageConceptProps['concept']['hero_creative_concept'] }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 p-4">
      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">
        {t('imageConcept.heroCreative')}
      </p>
      <p className="text-sm dark:text-neutral-200">{hero.description}</p>
      {hero.frech_elements.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {hero.frech_elements.map((el, i) => (
            <span key={i} className="rounded-full bg-amber-100 dark:bg-amber-800/40 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-300">{el}</span>
          ))}
        </div>
      )}
    </div>
  )
}

function RejectedConceptsList({ rejected }: { rejected: Array<{ type: string; reason: string }> }) {
  const { t } = useTranslation()
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 dark:text-neutral-400 mb-2">
        {t('imageConcept.rejectedConcepts')}
      </p>
      <div className="space-y-1">
        {rejected.map((rc, i) => (
          <div key={i} className="text-xs text-gray-400 dark:text-neutral-500">
            <span className="line-through">{rc.type}</span> — {rc.reason}
          </div>
        ))}
      </div>
    </div>
  )
}
